import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Stack,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Task, Space, TaskPayload } from '../../types/shared';
import { getTaskTypeConfig, TaskFieldConfig } from '../../config/taskTypeRegistry';
import { getLocalDateString } from '../../utils/dateUtils';

interface TaskCompletionDialogProps {
    open: boolean;
    task: Task | null;
    spaces: Space[];
    onClose: () => void;
    onComplete: (taskId: string, completionPayload: Partial<TaskPayload>) => void;
    isSubmitting?: boolean;
}

const TaskCompletionDialog: React.FC<TaskCompletionDialogProps> = ({
    open,
    task,
    spaces,
    onClose,
    onComplete,
    isSubmitting = false,
}) => {
    const { t } = useTranslation();
    const [formValues, setFormValues] = useState<Record<string, string>>({});

    const config = task ? getTaskTypeConfig(task.type) : null;
    const fields = config?.fields || [];

    // Initialize form values from the task's existing payload when dialog opens
    useEffect(() => {
        const today = getLocalDateString();
        if (open && task?.payload) {
            const initial: Record<string, string> = {};
            for (const field of fields) {
                let existing = (task.payload as Record<string, any>)?.[field.key];

                // Defaults for PLANT_SEED
                if (task.type === 'PLANT_SEED') {
                    if (field.key === 'plantingDate' && !existing) existing = today;
                    if (field.key === 'stage' && !existing) existing = 'seedling';
                }

                initial[field.key] = existing?.toString() ?? '';
            }
            setFormValues(initial);
        } else if (open) {
            const initial: Record<string, string> = {};
            for (const field of fields) {
                let defaultValue = '';

                // Defaults for PLANT_SEED
                if (task?.type === 'PLANT_SEED') {
                    if (field.key === 'plantingDate') defaultValue = today;
                    if (field.key === 'stage') defaultValue = 'seedling';
                }

                initial[field.key] = defaultValue;
            }
            setFormValues(initial);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, task]);

    const handleSubmit = () => {
        if (!task) return;
        // Convert quantity to number if present
        const processedValues: Record<string, any> = { ...formValues };
        if (processedValues.quantity) {
            processedValues.quantity = parseInt(processedValues.quantity, 10);
        }
        onComplete(task.id, processedValues as unknown as Partial<TaskPayload>);
    };

    const isValid = fields.every(f => !f.required || (formValues[f.key] && formValues[f.key].trim() !== ''));

    const renderField = (field: TaskFieldConfig) => {
        const value = formValues[field.key] || '';

        switch (field.type) {
            case 'space_select':
                return (
                    <FormControl key={field.key} fullWidth required={field.required}>
                        <InputLabel>{t(field.labelKey)}</InputLabel>
                        <Select
                            value={value}
                            label={t(field.labelKey)}
                            onChange={(e) => setFormValues(prev => ({
                                ...prev,
                                [field.key]: e.target.value as string,
                            }))}
                        >
                            {spaces.map(space => (
                                <MenuItem key={space.id} value={space.id}>
                                    {space.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                );

            case 'select':
                return (
                    <FormControl key={field.key} fullWidth required={field.required}>
                        <InputLabel>{t(field.labelKey)}</InputLabel>
                        <Select
                            value={value}
                            label={t(field.labelKey)}
                            onChange={(e) => setFormValues(prev => ({
                                ...prev,
                                [field.key]: e.target.value as string,
                            }))}
                        >
                            {field.options?.map(option => (
                                <MenuItem key={option.value} value={option.value}>
                                    {t(option.labelKey)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                );

            case 'textarea':
                return (
                    <TextField
                        key={field.key}
                        label={t(field.labelKey)}
                        fullWidth
                        multiline
                        rows={4}
                        required={field.required}
                        value={value}
                        onChange={(e) => setFormValues(prev => ({
                            ...prev,
                            [field.key]: e.target.value,
                        }))}
                    />
                );

            case 'date':
                return (
                    <TextField
                        key={field.key}
                        label={t(field.labelKey)}
                        type="date"
                        fullWidth
                        required={field.required}
                        value={value}
                        InputLabelProps={{ shrink: true }}
                        onChange={(e) => setFormValues(prev => ({
                            ...prev,
                            [field.key]: e.target.value,
                        }))}
                    />
                );

            case 'number':
                return (
                    <TextField
                        key={field.key}
                        label={t(field.labelKey)}
                        type="number"
                        fullWidth
                        required={field.required}
                        value={value}
                        onChange={(e) => setFormValues(prev => ({
                            ...prev,
                            [field.key]: e.target.value,
                        }))}
                    />
                );

            case 'text':
            default:
                return (
                    <TextField
                        key={field.key}
                        label={t(field.labelKey)}
                        fullWidth
                        required={field.required}
                        value={value}
                        onChange={(e) => setFormValues(prev => ({
                            ...prev,
                            [field.key]: e.target.value,
                        }))}
                    />
                );
        }
    };

    if (!task || !config) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{t('task_types.confirm_title')}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {task.title}
                    </Typography>
                    {task.description && (
                        <Typography variant="body2" color="text.secondary">
                            {task.description}
                        </Typography>
                    )}
                    {fields.map(renderField)}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={isSubmitting}>
                    {t('common.cancel')}
                </Button>
                <Button
                    variant="contained"
                    color="success"
                    onClick={handleSubmit}
                    disabled={isSubmitting || !isValid}
                >
                    {t(config.buttonLabelKey)}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TaskCompletionDialog;
